import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Divider, Tabs, Tab, Button } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index} style={{ width: '100%' }}>
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
);

const TermsAndConditions: React.FC = () => {
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const [tabValue, setTabValue] = useState(tabParam ? parseInt(tabParam) : 0);
    const [termsPages, setTermsPages] = useState(0);
    const [aupPages, setAupPages] = useState(0);
    const [termsPage, setTermsPage] = useState(1);
    const [aupPage, setAupPage] = useState(1);
    const [termsFile, setTermsFile] = useState<string | null>(null);
    const [aupFile, setAupFile] = useState<string | null>(null);

    useEffect(() => {
        // Load PDFs as Blob URLs (prevents ArrayBuffer detachment errors)
        Promise.all([
            fetch('/documents/TaC20260116.pdf').then(r => r.blob()).then(blob => URL.createObjectURL(blob)),
            fetch('/documents/AUP20260116.pdf').then(r => r.blob()).then(blob => URL.createObjectURL(blob))
        ]).then(([terms, aup]) => {
            setTermsFile(terms);
            setAupFile(aup);
        }).catch(err => console.error('Error loading PDFs:', err));
    }, []);

    useEffect(() => {
        // PDFs are accessed directly - Google Docs viewer will handle them cleanly
    }, []);

    useEffect(() => {
        if (tabParam) {
            setTabValue(parseInt(tabParam));
        }
    }, [tabParam]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    return (
        <Container maxWidth="md" sx={{ py: 4, boxSizing: 'border-box' }}>
            <Typography variant="h3" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
                Legal Documents
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                Last Updated: January 2026
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Terms and Conditions" />
                <Tab label="Acceptable Use Policy" />
            </Tabs>

            {/* Terms and Conditions Tab */}
            <TabPanel value={tabValue} index={0}>
                <Box sx={{ 
                    width: '100%', 
                    border: '1px solid #ddd', 
                    '& .react-pdf__text-layer': { display: 'none !important' },
                    '& .react-pdf__page__text': { display: 'none !important' }
                }}>
                    {termsFile ? (
                        <>
                            <Document file={termsFile} onLoadSuccess={({ numPages }) => setTermsPages(numPages)}>
                                <Page pageNumber={termsPage} width={550} renderTextLayer={false} renderAnnotationLayer={false} />
                            </Document>
                            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                                Page {termsPage} of {termsPages}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setTermsPage(Math.max(1, termsPage - 1))}
                                    disabled={termsPage === 1}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => setTermsPage(Math.min(termsPages, termsPage + 1))}
                                    disabled={termsPage === termsPages}
                                >
                                    Next
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Typography>Loading...</Typography>
                    )}
                </Box>
            </TabPanel>

            {/* Acceptable Use Policy Tab */}
            <TabPanel value={tabValue} index={1}>
                <Box sx={{ 
                    width: '100%', 
                    border: '1px solid #ddd', 
                    p: 2,
                    '& .react-pdf__text-layer': { display: 'none !important' },
                    '& .react-pdf__page__text': { display: 'none !important' }
                }}>
                    {aupFile ? (
                        <>
                            <Document file={aupFile} onLoadSuccess={({ numPages }) => setAupPages(numPages)}>
                                <Page pageNumber={aupPage} width={550} renderTextLayer={false} renderAnnotationLayer={false} />
                            </Document>
                            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                                Page {aupPage} of {aupPages}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setAupPage(Math.max(1, aupPage - 1))}
                                    disabled={aupPage === 1}
                                >
                                    Previous
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => setAupPage(Math.min(aupPages, aupPage + 1))}
                                    disabled={aupPage === aupPages}
                                >
                                    Next
                                </Button>
                            </Box>
                        </>
                    ) : (
                        <Typography>Loading...</Typography>
                    )}
                </Box>
            </TabPanel>
        </Container>
    );
};

export default TermsAndConditions;
